import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';

import type { MemberGender } from '@/lib/gender';
import { normalizeMemberTextField } from '@/utils/member-text-normalize';

/** Fields the import API accepts on each row — mirrors the add-person form. */
export type ImportField =
  | 'membershipNumber'
  | 'firstName'
  | 'middleName'
  | 'lastName'
  | 'maidenName'
  | 'title'
  | 'gender'
  | 'dateOfBirth'
  | 'placeOfBirth'
  | 'maritalStatus'
  | 'isCommunicant'
  | 'motherName'
  | 'fatherName'
  | 'occupation'
  | 'employer'
  | 'educationBackground'
  | 'employmentBackground'
  | 'nationalId'
  | 'email'
  | 'phone'
  | 'appConnectEmail'
  | 'appConnectPhone'
  | 'alternatePhone'
  | 'address'
  | 'city'
  | 'region'
  | 'country'
  | 'postalAddress'
  | 'postalCity'
  | 'postalRegion'
  | 'postalCountry'
  | 'hometown'
  | 'hometownRegion'
  | 'memberType'
  | 'status'
  | 'receptionMethod'
  | 'formerChurch'
  | 'joinedAt'
  | 'baptismDate'
  | 'baptismNumber'
  | 'baptismPlace'
  | 'baptismMinister'
  | 'baptismType'
  | 'confirmationDate'
  | 'confirmationNumber'
  | 'confirmationPlace'
  | 'marriageDate'
  | 'marriagePlace'
  | 'marriageType'
  | 'marriageMinister'
  | 'spouseNameManual'
  | 'emergencyContactName'
  | 'emergencyContactPhone'
  | 'emergencyContactRelationship'
  | 'isDistantMember'
  | 'hasDisability'
  | 'disabilityNotes'
  | 'notes';

export type ImportFieldGroup =
  | 'identity'
  | 'contact'
  | 'appConnect'
  | 'membership'
  | 'marriage'
  | 'emergency'
  | 'care';

export const IMPORT_FIELD_GROUPS: Record<ImportField, ImportFieldGroup> = {
  membershipNumber: 'identity',
  firstName: 'identity',
  middleName: 'identity',
  lastName: 'identity',
  maidenName: 'identity',
  title: 'identity',
  gender: 'identity',
  dateOfBirth: 'identity',
  placeOfBirth: 'identity',
  maritalStatus: 'identity',
  isCommunicant: 'identity',
  motherName: 'identity',
  fatherName: 'identity',
  occupation: 'identity',
  employer: 'identity',
  educationBackground: 'identity',
  employmentBackground: 'identity',
  nationalId: 'identity',
  email: 'contact',
  phone: 'contact',
  appConnectEmail: 'appConnect',
  appConnectPhone: 'appConnect',
  alternatePhone: 'contact',
  address: 'contact',
  city: 'contact',
  region: 'contact',
  country: 'contact',
  postalAddress: 'contact',
  postalCity: 'contact',
  postalRegion: 'contact',
  postalCountry: 'contact',
  hometown: 'contact',
  hometownRegion: 'contact',
  memberType: 'membership',
  status: 'membership',
  receptionMethod: 'membership',
  formerChurch: 'membership',
  joinedAt: 'membership',
  baptismDate: 'membership',
  baptismNumber: 'membership',
  baptismPlace: 'membership',
  baptismMinister: 'membership',
  baptismType: 'membership',
  confirmationDate: 'membership',
  confirmationNumber: 'membership',
  confirmationPlace: 'membership',
  marriageDate: 'marriage',
  marriagePlace: 'marriage',
  marriageType: 'marriage',
  marriageMinister: 'marriage',
  spouseNameManual: 'marriage',
  emergencyContactName: 'emergency',
  emergencyContactPhone: 'emergency',
  emergencyContactRelationship: 'emergency',
  isDistantMember: 'care',
  hasDisability: 'care',
  disabilityNotes: 'care',
  notes: 'care',
};

/** i18n keys under the `people` namespace — sentence-case labels in `import.fields`. */
export const IMPORT_FIELD_LABEL_KEYS: Record<ImportField, string> = {
  membershipNumber: 'import.fields.membershipNumber',
  firstName: 'import.fields.firstName',
  middleName: 'import.fields.middleName',
  lastName: 'import.fields.lastName',
  maidenName: 'import.fields.maidenName',
  title: 'import.fields.title',
  gender: 'import.fields.gender',
  dateOfBirth: 'import.fields.dateOfBirth',
  placeOfBirth: 'import.fields.placeOfBirth',
  maritalStatus: 'import.fields.maritalStatus',
  isCommunicant: 'import.fields.isCommunicant',
  motherName: 'import.fields.motherName',
  fatherName: 'import.fields.fatherName',
  occupation: 'import.fields.occupation',
  employer: 'import.fields.employer',
  educationBackground: 'import.fields.educationBackground',
  employmentBackground: 'import.fields.employmentBackground',
  nationalId: 'import.fields.nationalId',
  email: 'import.fields.email',
  phone: 'import.fields.phone',
  appConnectEmail: 'import.fields.appConnectEmail',
  appConnectPhone: 'import.fields.appConnectPhone',
  alternatePhone: 'import.fields.alternatePhone',
  address: 'import.fields.address',
  city: 'import.fields.city',
  region: 'import.fields.region',
  country: 'import.fields.country',
  postalAddress: 'import.fields.postalAddress',
  postalCity: 'import.fields.postalCity',
  postalRegion: 'import.fields.postalRegion',
  postalCountry: 'import.fields.postalCountry',
  hometown: 'import.fields.hometown',
  hometownRegion: 'import.fields.hometownRegion',
  memberType: 'import.fields.memberType',
  status: 'import.fields.status',
  receptionMethod: 'import.fields.receptionMethod',
  formerChurch: 'import.fields.formerChurch',
  joinedAt: 'import.fields.joinedAt',
  baptismDate: 'import.fields.baptismDate',
  baptismNumber: 'import.fields.baptismNumber',
  baptismPlace: 'import.fields.baptismPlace',
  baptismMinister: 'import.fields.baptismMinister',
  baptismType: 'import.fields.baptismType',
  confirmationDate: 'import.fields.confirmationDate',
  confirmationNumber: 'import.fields.confirmationNumber',
  confirmationPlace: 'import.fields.confirmationPlace',
  marriageDate: 'import.fields.marriageDate',
  marriagePlace: 'import.fields.marriagePlace',
  marriageType: 'import.fields.marriageType',
  marriageMinister: 'import.fields.marriageMinister',
  spouseNameManual: 'import.fields.spouseNameManual',
  emergencyContactName: 'import.fields.emergencyContactName',
  emergencyContactPhone: 'import.fields.emergencyContactPhone',
  emergencyContactRelationship: 'import.fields.emergencyContactRelationship',
  isDistantMember: 'import.fields.isDistantMember',
  hasDisability: 'import.fields.hasDisability',
  disabilityNotes: 'import.fields.disabilityNotes',
  notes: 'import.fields.notes',
};

export const IMPORT_FIELD_GROUP_LABEL_KEYS: Record<ImportFieldGroup, string> = {
  identity: 'import.groups.identity',
  contact: 'import.groups.contact',
  appConnect: 'import.groups.appConnect',
  membership: 'import.groups.membership',
  marriage: 'import.groups.marriage',
  emergency: 'import.groups.emergency',
  care: 'import.groups.care',
};

export const IMPORT_FIELDS: ImportField[] = [
  'membershipNumber',
  'firstName',
  'middleName',
  'lastName',
  'maidenName',
  'title',
  'gender',
  'dateOfBirth',
  'placeOfBirth',
  'maritalStatus',
  'isCommunicant',
  'motherName',
  'fatherName',
  'occupation',
  'employer',
  'educationBackground',
  'employmentBackground',
  'nationalId',
  'email',
  'phone',
  'appConnectEmail',
  'appConnectPhone',
  'alternatePhone',
  'address',
  'city',
  'region',
  'country',
  'postalAddress',
  'postalCity',
  'postalRegion',
  'postalCountry',
  'hometown',
  'hometownRegion',
  'memberType',
  'status',
  'receptionMethod',
  'formerChurch',
  'joinedAt',
  'baptismDate',
  'baptismNumber',
  'baptismPlace',
  'baptismMinister',
  'baptismType',
  'confirmationDate',
  'confirmationNumber',
  'confirmationPlace',
  'marriageDate',
  'marriagePlace',
  'marriageType',
  'marriageMinister',
  'spouseNameManual',
  'emergencyContactName',
  'emergencyContactPhone',
  'emergencyContactRelationship',
  'isDistantMember',
  'hasDisability',
  'disabilityNotes',
  'notes',
];

export const REQUIRED_IMPORT_FIELDS: ImportField[] = ['firstName', 'lastName'];

export const MAX_IMPORT_ROWS = 500;

export type MemberType = 'first_timer' | 'visitor' | 'member';
export type MemberStatus = 'active' | 'inactive' | 'transferred' | 'deceased';
export type Gender = MemberGender;
export type MaritalStatus =
  | 'single'
  | 'married'
  | 'separated'
  | 'divorced'
  | 'widowed'
  | 'cohabiting'
  | 'other';
export type ReceptionMethod =
  | 'profession'
  | 'confirmation'
  | 'transfer'
  | 'reaffirmation'
  | 'restoration'
  | 'birth'
  | 'other';
export type BaptismType = 'infant' | 'adult' | 'unknown';
export type MarriageType = 'customary' | 'ordinance' | 'civil' | 'both' | 'other';

export interface ImportRowPayload {
  membershipNumber?: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  maidenName?: string;
  title?: string;
  gender?: Gender;
  dateOfBirth?: string;
  placeOfBirth?: string;
  maritalStatus?: MaritalStatus;
  isCommunicant?: boolean | null;
  motherName?: string;
  fatherName?: string;
  occupation?: string;
  employer?: string;
  educationBackground?: string;
  employmentBackground?: string;
  nationalId?: string;
  email?: string;
  phone?: string;
  appConnectEmailSameAsEmail?: boolean;
  appConnectPhoneSameAsPhone?: boolean;
  appConnectEmail?: string;
  appConnectPhone?: string;
  alternatePhone?: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  postalAddress?: string;
  postalCity?: string;
  postalRegion?: string;
  postalCountry?: string;
  hometown?: string;
  hometownRegion?: string;
  memberType?: MemberType;
  status?: MemberStatus;
  receptionMethod?: ReceptionMethod;
  formerChurch?: string;
  joinedAt?: string;
  baptismDate?: string;
  baptismNumber?: string;
  baptismPlace?: string;
  baptismMinister?: string;
  baptismType?: BaptismType;
  confirmationDate?: string;
  confirmationNumber?: string;
  confirmationPlace?: string;
  marriageDate?: string;
  marriagePlace?: string;
  marriageType?: MarriageType;
  marriageMinister?: string;
  spouseNameManual?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelationship?: string;
  isDistantMember?: boolean;
  hasDisability?: boolean;
  disabilityNotes?: string;
  notes?: string;
}

export type RowIssue =
  | 'missingName'
  | 'invalidEmail'
  | 'invalidPhone'
  | 'invalidAppConnectEmail'
  | 'invalidAppConnectPhone'
  | 'invalidAlternatePhone'
  | 'invalidEmergencyPhone'
  | 'invalidMemberType'
  | 'invalidMemberStatus'
  | 'invalidGender'
  | 'invalidMaritalStatus'
  | 'invalidReceptionMethod'
  | 'invalidBaptismType'
  | 'invalidMarriageType'
  | 'invalidCountry'
  | 'invalidDate'
  | 'invalidBoolean'
  | 'firstTimerPhone';

export interface PreparedImportRow {
  index: number;
  source: Record<string, string>;
  payload: ImportRowPayload | null;
  issues: RowIssue[];
}

export interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
}

const HEADER_ALIASES: Record<ImportField, string[]> = {
  membershipNumber: [
    'membershipnumber',
    'membership_number',
    'membership number',
    'membernumber',
    'member_number',
    'member number',
  ],
  firstName: ['firstname', 'first_name', 'first name', 'givenname', 'given_name', 'given name'],
  middleName: ['middlename', 'middle_name', 'middle name'],
  lastName: [
    'lastname',
    'last_name',
    'last name',
    'surname',
    'familyname',
    'family_name',
    'family name',
  ],
  maidenName: ['maidenname', 'maiden_name', 'maiden name'],
  title: ['title', 'honorific', 'prefix'],
  gender: ['gender', 'sex'],
  dateOfBirth: [
    'dateofbirth',
    'date_of_birth',
    'date of birth',
    'dob',
    'birthdate',
    'birth_date',
    'birth date',
  ],
  placeOfBirth: ['placeofbirth', 'place_of_birth', 'place of birth', 'birthplace', 'birth place'],
  maritalStatus: ['maritalstatus', 'marital_status', 'marital status', 'marital'],
  isCommunicant: ['iscommunicant', 'is_communicant', 'communicant'],
  motherName: ['mothername', 'mother_name', "mother's name", 'mother'],
  fatherName: ['fathername', 'father_name', "father's name", 'father'],
  occupation: ['occupation', 'job', 'profession'],
  employer: ['employer', 'workplace', 'work place', 'company'],
  educationBackground: ['education', 'educationbackground', 'education_background'],
  employmentBackground: [
    'employmentbackground',
    'employment_background',
    'employment history',
    'work history',
  ],
  nationalId: [
    'nationalid',
    'national_id',
    'national id',
    'ghana card',
    'ghanacard',
    'id number',
    'idnumber',
  ],
  email: ['email', 'emailaddress', 'email_address', 'email address', 'e-mail'],
  appConnectEmail: [
    'appconnectemail',
    'app_connect_email',
    'app connect email',
    'app connection email',
    'login email',
  ],
  appConnectPhone: [
    'appconnectphone',
    'app_connect_phone',
    'app connect phone',
    'app connection phone',
    'login phone',
  ],
  phone: [
    'phone',
    'phonenumber',
    'phone_number',
    'phone number',
    'mobile',
    'cellphone',
    'cell',
    'tel',
  ],
  alternatePhone: [
    'alternatephone',
    'alternate_phone',
    'alternate phone',
    'other phone',
    'otherphone',
    'secondaryphone',
    'secondary_phone',
    'secondary phone',
    'second phone',
    'secondphone',
    'second_phone',
  ],
  address: ['address', 'street', 'street address', 'home address'],
  city: ['city', 'town', 'city or town', 'city/town'],
  region: ['region', 'state', 'province'],
  country: ['country', 'country code'],
  postalAddress: ['postaladdress', 'postal_address', 'postal address', 'mailing address'],
  postalCity: ['postalcity', 'postal_city', 'postal city'],
  postalRegion: ['postalregion', 'postal_region', 'postal region'],
  postalCountry: ['postalcountry', 'postal_country', 'postal country'],
  hometown: ['hometown', 'home_town', 'home town'],
  hometownRegion: [
    'hometownregion',
    'hometown_region',
    'hometown region',
    'home town region',
  ],
  memberType: [
    'membertype',
    'member_type',
    'member type',
    'type',
    'persontype',
    'person_type',
    'person type',
  ],
  status: [
    'status',
    'memberstatus',
    'member_status',
    'member status',
    'membershipstatus',
    'membership_status',
    'membership status',
  ],
  receptionMethod: [
    'receptionmethod',
    'reception_method',
    'reception method',
    'how received',
    'received how',
  ],
  formerChurch: [
    'formerchurch',
    'former_church',
    'former church',
    'previous church',
    'previous congregation',
    'previouscongregation',
  ],
  joinedAt: [
    'joinedat',
    'joined_at',
    'joined at',
    'date received',
    'datereceived',
    'join date',
    'joindate',
    'membership date',
  ],
  baptismDate: ['baptismdate', 'baptism_date', 'baptism date', 'date of baptism'],
  baptismNumber: ['baptismnumber', 'baptism_number', 'baptism number'],
  baptismPlace: ['baptismplace', 'baptism_place', 'baptism place'],
  baptismMinister: [
    'baptismminister',
    'baptism_minister',
    'baptism minister',
    'baptising minister',
    'baptizing minister',
  ],
  baptismType: ['baptismtype', 'baptism_type', 'baptism type'],
  confirmationDate: [
    'confirmationdate',
    'confirmation_date',
    'confirmation date',
    'date of confirmation',
  ],
  confirmationNumber: ['confirmationnumber', 'confirmation_number', 'confirmation number'],
  confirmationPlace: [
    'confirmationplace',
    'confirmation_place',
    'confirmation place',
  ],
  marriageDate: ['marriagedate', 'marriage_date', 'marriage date', 'wedding date', 'weddingdate'],
  marriagePlace: ['marriageplace', 'marriage_place', 'marriage place', 'wedding place'],
  marriageType: ['marriagetype', 'marriage_type', 'marriage type'],
  marriageMinister: [
    'marriageminister',
    'marriage_minister',
    'marriage minister',
    'officiating minister',
    'wedding minister',
  ],
  spouseNameManual: ['spouse', 'spouse name', 'spousename', 'spouse_name', 'partner'],
  emergencyContactName: [
    'emergencycontactname',
    'emergency_contact_name',
    'emergency contact',
    'emergency contact name',
    'next of kin',
    'nextofkin',
  ],
  emergencyContactPhone: [
    'emergencycontactphone',
    'emergency_contact_phone',
    'emergency contact phone',
    'next of kin phone',
  ],
  emergencyContactRelationship: [
    'emergencycontactrelationship',
    'emergency_contact_relationship',
    'emergency contact relationship',
    'relationship to contact',
    'next of kin relationship',
  ],
  isDistantMember: [
    'isdistantmember',
    'is_distant_member',
    'distant member',
    'distantmember',
    'lives away',
  ],
  hasDisability: [
    'hasdisability',
    'has_disability',
    'disability',
    'accessibility',
    'needs accessibility',
  ],
  disabilityNotes: [
    'disabilitynotes',
    'disability_notes',
    'disability notes',
    'accessibility notes',
  ],
  notes: ['notes', 'note', 'comments', 'comment', 'remarks'],
};

const normalizeHeader = (value: string): string =>
  value.replace(/^\uFEFF/, '').trim().toLowerCase().replace(/\s+/g, ' ');

/** Guess which import field a CSV header maps to. */
export const guessFieldForHeader = (header: string): ImportField | null => {
  const key = normalizeHeader(header);
  if (!key) return null;
  for (const field of IMPORT_FIELDS) {
    if (HEADER_ALIASES[field].includes(key) || key === field.toLowerCase()) return field;
  }
  return null;
};

export const buildInitialMapping = (headers: string[]): Record<string, ImportField | ''> => {
  const mapping: Record<string, ImportField | ''> = {};
  const used = new Set<ImportField>();
  for (const header of headers) {
    const guess = guessFieldForHeader(header);
    if (guess && !used.has(guess)) {
      mapping[header] = guess;
      used.add(guess);
    } else {
      mapping[header] = '';
    }
  }
  return mapping;
};

/** Minimal RFC4180-ish CSV parse (quoted fields, commas, CRLF). */
export const parseCsv = (text: string): ParsedCsv => {
  const rows: string[][] = [];
  let current: string[] = [];
  let cell = '';
  let inQuotes = false;

  const pushCell = () => {
    current.push(cell);
    cell = '';
  };
  const pushRow = () => {
    if (current.length === 1 && current[0] === '' && rows.length > 0) {
      current = [];
      return;
    }
    rows.push(current);
    current = [];
  };

  const input = text.replace(/^\uFEFF/, '');
  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i]!;
    const next = input[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      pushCell();
    } else if (ch === '\n') {
      pushCell();
      pushRow();
    } else if (ch === '\r') {
      // ignore
    } else {
      cell += ch;
    }
  }
  pushCell();
  if (current.length > 1 || current[0] !== '') pushRow();

  if (rows.length === 0) return { headers: [], rows: [] };

  const headers = rows[0]!.map((h) => h.trim());
  const data = rows.slice(1).map((values) => {
    const record: Record<string, string> = {};
    headers.forEach((header, index) => {
      record[header] = (values[index] ?? '').trim();
    });
    return record;
  });

  return { headers, rows: data.filter((row) => Object.values(row).some((value) => value !== '')) };
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const E164_RE = /^\+[1-9]\d{6,18}$/;
const COUNTRY_RE = /^[A-Z]{2}$/;

const EMPTY_IMPORT_VALUES = new Set([
  '-',
  '--',
  '—',
  '–',
  '.',
  'n/a',
  'n.a',
  'n.a.',
  'none',
  'nil',
  'null',
  'missing',
  'unknown',
  'unk',
  'tbd',
  'tba',
  'blank',
  'empty',
  'no email',
  'no phone',
  'not available',
  'not applicable',
]);

/** Spreadsheet placeholders that mean “no value” — not invalid email or phone. */
export const isBlankImportCell = (raw: string): boolean => {
  const trimmed = raw.trim();
  if (!trimmed) return true;
  return EMPTY_IMPORT_VALUES.has(trimmed.toLowerCase().replace(/\s+/g, ' '));
};

export const normalizeImportCell = (raw: string): string =>
  isBlankImportCell(raw) ? '' : raw.trim();

export const normalizePhone = (raw: string, defaultCountry: CountryCode): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (E164_RE.test(trimmed)) return trimmed;
  const parsed = parsePhoneNumberFromString(trimmed, defaultCountry);
  if (parsed?.isValid()) return parsed.format('E.164');
  return null;
};

const slug = (raw: string): string => raw.trim().toLowerCase().replace(/[\s-]+/g, '_');

const normalizeMemberType = (raw: string): MemberType | null => {
  const key = slug(raw);
  if (!key) return null;
  if (key === 'member' || key === 'members') return 'member';
  if (key === 'visitor' || key === 'visitors') return 'visitor';
  if (
    key === 'first_timer' ||
    key === 'firsttimer' ||
    key === 'first_time' ||
    key === 'first_timers'
  ) {
    return 'first_timer';
  }
  return null;
};

const normalizeMemberStatus = (raw: string): MemberStatus | null => {
  const key = slug(raw);
  if (!key) return null;
  const aliases: Record<string, MemberStatus> = {
    active: 'active',
    inactive: 'inactive',
    transferred: 'transferred',
    transfer: 'transferred',
    deceased: 'deceased',
    dead: 'deceased',
    died: 'deceased',
  };
  return aliases[key] ?? null;
};

const normalizeGender = (raw: string): Gender | null => {
  const key = slug(raw);
  if (!key) return null;
  if (key === 'male' || key === 'm') return 'male';
  if (key === 'female' || key === 'f') return 'female';
  return null;
};

const normalizeMaritalStatus = (raw: string): MaritalStatus | null => {
  const key = slug(raw);
  if (!key) return null;
  const aliases: Record<string, MaritalStatus> = {
    single: 'single',
    married: 'married',
    separated: 'separated',
    divorced: 'divorced',
    widowed: 'widowed',
    cohabiting: 'other',
    cohabitation: 'other',
    living_together: 'other',
    other: 'other',
  };
  return aliases[key] ?? null;
};

const normalizeReceptionMethod = (raw: string): ReceptionMethod | null => {
  const key = slug(raw);
  if (!key) return null;
  const aliases: Record<string, ReceptionMethod> = {
    profession: 'profession',
    profession_of_faith: 'profession',
    confirmation: 'confirmation',
    transfer: 'transfer',
    reaffirmation: 'reaffirmation',
    reaffirmation_of_faith: 'reaffirmation',
    restoration: 'restoration',
    birth: 'birth',
    child_of_the_church: 'birth',
    other: 'other',
  };
  return aliases[key] ?? null;
};

const normalizeBaptismType = (raw: string): BaptismType | null => {
  const key = slug(raw);
  if (!key) return null;
  if (key === 'infant' || key === 'child') return 'infant';
  if (key === 'adult') return 'adult';
  if (key === 'unknown' || key === 'not_sure' || key === 'unsure') return 'unknown';
  return null;
};

const normalizeMarriageType = (raw: string): MarriageType | null => {
  const key = slug(raw);
  if (!key) return null;
  const aliases: Record<string, MarriageType> = {
    customary: 'customary',
    ordinance: 'ordinance',
    church_blessing: 'ordinance',
    civil: 'civil',
    both: 'both',
    customary_and_church: 'both',
    other: 'other',
  };
  return aliases[key] ?? null;
};

const normalizeCountry = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const upper = trimmed.toUpperCase();
  if (COUNTRY_RE.test(upper)) return upper;
  const aliases: Record<string, string> = {
    ghana: 'GH',
    nigeria: 'NG',
    'united kingdom': 'GB',
    uk: 'GB',
    'united states': 'US',
    usa: 'US',
    america: 'US',
  };
  return aliases[trimmed.toLowerCase()] ?? null;
};

const normalizeDate = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const slash = trimmed.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (slash) {
    const day = Number(slash[1]);
    const month = Number(slash[2]);
    const year = slash[3]!;
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }
  return null;
};

const normalizeBoolean = (raw: string): boolean | null => {
  const key = slug(raw);
  if (!key) return null;
  if (['yes', 'y', 'true', '1', 'on'].includes(key)) return true;
  if (['no', 'n', 'false', '0', 'off'].includes(key)) return false;
  return null;
};

type OptionalStringPayloadKey = {
  [K in keyof ImportRowPayload]-?: string extends NonNullable<ImportRowPayload[K]> ? K : never;
}[keyof ImportRowPayload];

const TEXT_FIELDS: Array<{
  field: ImportField;
  key: OptionalStringPayloadKey;
  max: number;
}> = [
  { field: 'membershipNumber', key: 'membershipNumber', max: 50 },
  { field: 'middleName', key: 'middleName', max: 100 },
  { field: 'maidenName', key: 'maidenName', max: 100 },
  { field: 'title', key: 'title', max: 40 },
  { field: 'placeOfBirth', key: 'placeOfBirth', max: 200 },
  { field: 'motherName', key: 'motherName', max: 200 },
  { field: 'fatherName', key: 'fatherName', max: 200 },
  { field: 'occupation', key: 'occupation', max: 120 },
  { field: 'employer', key: 'employer', max: 120 },
  { field: 'educationBackground', key: 'educationBackground', max: 2000 },
  { field: 'employmentBackground', key: 'employmentBackground', max: 2000 },
  { field: 'nationalId', key: 'nationalId', max: 40 },
  { field: 'address', key: 'address', max: 500 },
  { field: 'city', key: 'city', max: 120 },
  { field: 'region', key: 'region', max: 120 },
  { field: 'postalAddress', key: 'postalAddress', max: 500 },
  { field: 'postalCity', key: 'postalCity', max: 120 },
  { field: 'postalRegion', key: 'postalRegion', max: 120 },
  { field: 'hometown', key: 'hometown', max: 200 },
  { field: 'hometownRegion', key: 'hometownRegion', max: 120 },
  { field: 'formerChurch', key: 'formerChurch', max: 200 },
  { field: 'baptismNumber', key: 'baptismNumber', max: 80 },
  { field: 'baptismPlace', key: 'baptismPlace', max: 200 },
  { field: 'baptismMinister', key: 'baptismMinister', max: 200 },
  { field: 'confirmationNumber', key: 'confirmationNumber', max: 80 },
  { field: 'confirmationPlace', key: 'confirmationPlace', max: 200 },
  { field: 'marriagePlace', key: 'marriagePlace', max: 200 },
  { field: 'marriageMinister', key: 'marriageMinister', max: 200 },
  { field: 'spouseNameManual', key: 'spouseNameManual', max: 200 },
  { field: 'emergencyContactName', key: 'emergencyContactName', max: 200 },
  { field: 'emergencyContactRelationship', key: 'emergencyContactRelationship', max: 80 },
  { field: 'disabilityNotes', key: 'disabilityNotes', max: 1000 },
  { field: 'notes', key: 'notes', max: 4000 },
];

const DATE_FIELDS: Array<{ field: ImportField; key: OptionalStringPayloadKey }> = [
  { field: 'dateOfBirth', key: 'dateOfBirth' },
  { field: 'joinedAt', key: 'joinedAt' },
  { field: 'baptismDate', key: 'baptismDate' },
  { field: 'confirmationDate', key: 'confirmationDate' },
  { field: 'marriageDate', key: 'marriageDate' },
];

export interface ImportAppConnectOptions {
  appConnectEmailSameAsEmail: boolean;
  appConnectPhoneSameAsPhone: boolean;
}

export const prepareImportRows = (input: {
  rows: Record<string, string>[];
  mapping: Record<string, ImportField | ''>;
  defaultCountry: CountryCode;
  appConnect?: ImportAppConnectOptions;
}): PreparedImportRow[] => {
  const fieldToHeader = new Map<ImportField, string>();
  for (const [header, field] of Object.entries(input.mapping)) {
    if (field) fieldToHeader.set(field, header);
  }

  return input.rows.map((source, index) => {
    const get = (field: ImportField): string => {
      const header = fieldToHeader.get(field);
      return normalizeImportCell(header ? (source[header] ?? '') : '');
    };

    const issues: RowIssue[] = [];
    const payload: ImportRowPayload = {
      firstName: normalizeMemberTextField('firstName', get('firstName')) ?? '',
      lastName: normalizeMemberTextField('lastName', get('lastName')) ?? '',
    };

    if (!payload.firstName || !payload.lastName) issues.push('missingName');

    const emailRaw = get('email');
    if (emailRaw) {
      if (EMAIL_RE.test(emailRaw)) payload.email = emailRaw.toLowerCase();
      else issues.push('invalidEmail');
    }

    const phoneRaw = get('phone');
    if (phoneRaw) {
      const normalized = normalizePhone(phoneRaw, input.defaultCountry);
      if (normalized) payload.phone = normalized;
      else issues.push('invalidPhone');
    }

    const altPhoneRaw = get('alternatePhone');
    if (altPhoneRaw) {
      const normalized = normalizePhone(altPhoneRaw, input.defaultCountry);
      if (normalized) payload.alternatePhone = normalized;
      else issues.push('invalidAlternatePhone');
    }

    const emergencyPhoneRaw = get('emergencyContactPhone');
    if (emergencyPhoneRaw) {
      const normalized = normalizePhone(emergencyPhoneRaw, input.defaultCountry);
      if (normalized) payload.emergencyContactPhone = normalized;
      else issues.push('invalidEmergencyPhone');
    }

    const appConnect = input.appConnect ?? {
      appConnectEmailSameAsEmail: true,
      appConnectPhoneSameAsPhone: true,
    };
    payload.appConnectEmailSameAsEmail = appConnect.appConnectEmailSameAsEmail;
    payload.appConnectPhoneSameAsPhone = appConnect.appConnectPhoneSameAsPhone;

    if (!appConnect.appConnectEmailSameAsEmail) {
      const appEmailRaw = get('appConnectEmail');
      if (appEmailRaw) {
        if (EMAIL_RE.test(appEmailRaw)) payload.appConnectEmail = appEmailRaw.toLowerCase();
        else issues.push('invalidAppConnectEmail');
      }
    }

    if (!appConnect.appConnectPhoneSameAsPhone) {
      const appPhoneRaw = get('appConnectPhone');
      if (appPhoneRaw) {
        const normalized = normalizePhone(appPhoneRaw, input.defaultCountry);
        if (normalized) payload.appConnectPhone = normalized;
        else issues.push('invalidAppConnectPhone');
      }
    }

    const memberTypeRaw = get('memberType');
    if (memberTypeRaw) {
      const normalized = normalizeMemberType(memberTypeRaw);
      if (normalized) payload.memberType = normalized;
      else issues.push('invalidMemberType');
    }

    const statusRaw = get('status');
    if (statusRaw) {
      const normalized = normalizeMemberStatus(statusRaw);
      if (normalized) payload.status = normalized;
      else issues.push('invalidMemberStatus');
    }

    const genderRaw = get('gender');
    if (genderRaw) {
      const normalized = normalizeGender(genderRaw);
      if (normalized) payload.gender = normalized;
      else issues.push('invalidGender');
    }

    const maritalRaw = get('maritalStatus');
    if (maritalRaw) {
      const normalized = normalizeMaritalStatus(maritalRaw);
      if (normalized) payload.maritalStatus = normalized;
      else issues.push('invalidMaritalStatus');
    }

    const receptionRaw = get('receptionMethod');
    if (receptionRaw) {
      const normalized = normalizeReceptionMethod(receptionRaw);
      if (normalized) payload.receptionMethod = normalized;
      else issues.push('invalidReceptionMethod');
    }

    const baptismTypeRaw = get('baptismType');
    if (baptismTypeRaw) {
      const normalized = normalizeBaptismType(baptismTypeRaw);
      if (normalized) payload.baptismType = normalized;
      else issues.push('invalidBaptismType');
    }

    const marriageTypeRaw = get('marriageType');
    if (marriageTypeRaw) {
      const normalized = normalizeMarriageType(marriageTypeRaw);
      if (normalized) payload.marriageType = normalized;
      else issues.push('invalidMarriageType');
    }

    const countryRaw = get('country');
    if (countryRaw) {
      const normalized = normalizeCountry(countryRaw);
      if (normalized) payload.country = normalized;
      else issues.push('invalidCountry');
    }

    const postalCountryRaw = get('postalCountry');
    if (postalCountryRaw) {
      const normalized = normalizeCountry(postalCountryRaw);
      if (normalized) payload.postalCountry = normalized;
      else issues.push('invalidCountry');
    }

    const communicantRaw = get('isCommunicant');
    if (communicantRaw) {
      const normalized = normalizeBoolean(communicantRaw);
      if (normalized === null) issues.push('invalidBoolean');
      else payload.isCommunicant = normalized;
    }

    for (const { field, key } of DATE_FIELDS) {
      const raw = get(field);
      if (!raw) continue;
      const normalized = normalizeDate(raw);
      if (normalized) payload[key] = normalized;
      else if (!issues.includes('invalidDate')) issues.push('invalidDate');
    }

    for (const { field, key, max } of TEXT_FIELDS) {
      const raw = get(field);
      if (!raw) continue;
      const normalized = normalizeMemberTextField(field, raw.slice(0, max));
      if (normalized) payload[key] = normalized;
    }

    const distantRaw = get('isDistantMember');
    if (distantRaw) {
      const normalized = normalizeBoolean(distantRaw);
      if (normalized === null) issues.push('invalidBoolean');
      else payload.isDistantMember = normalized;
    }

    const disabilityRaw = get('hasDisability');
    if (disabilityRaw) {
      const normalized = normalizeBoolean(disabilityRaw);
      if (normalized === null) issues.push('invalidBoolean');
      else payload.hasDisability = normalized;
    }

    const resolvedType = payload.memberType ?? 'member';
    if (resolvedType === 'first_timer' && !payload.phone) issues.push('firstTimerPhone');

    if (issues.length > 0) {
      return { index, source, payload: null, issues };
    }

    return { index, source, payload, issues: [] };
  });
};

export const SAMPLE_CSV = [
  [
    'Membership number',
    'First name',
    'Last name',
    'Title',
    'Email',
    'Phone',
    'Secondary phone',
    'Member type',
    'Membership status',
    'Gender',
    'Date of birth',
    'Marital status',
    'Communicant',
    'Occupation',
    'Address',
    'City or town',
    'Region',
    'Country',
    'Hometown',
    'Date received',
    'Baptism date',
    'Emergency contact',
    'Emergency contact phone',
    'Notes',
  ].join(','),
  [
    '1001',
    'Ama',
    'Mensah',
    'Mrs',
    'ama.mensah@example.com',
    '+233201234567',
    '0244123456',
    'member',
    'active',
    'female',
    '1990-05-12',
    'married',
    'yes',
    'Teacher',
    '12 Independence Ave',
    'Accra',
    'Greater Accra',
    'GH',
    'Kumasi',
    '2015-03-01',
    '1990-06-01',
    'Kofi Mensah',
    '+233244000111',
    '',
  ].join(','),
  [
    '1002',
    'Kwame',
    'Asante',
    'Mr',
    '',
    '0201234568',
    '',
    'visitor',
    'inactive',
    'male',
    '1985-11-03',
    'single',
    'no',
    'Engineer',
    '',
    'Accra',
    '',
    'GH',
    'Accra',
    '',
    '',
    '',
    '',
    '',
  ].join(','),
].join('\n');
